import React, { useEffect, useState } from 'react';
import * as echarts from 'echarts';
import { commonEchartsLabelFormatter, commonEchartsTooltipFormatter } from '../../../common/util/CommonEcharts.util';
import { ECBasicOption } from 'echarts/types/dist/shared';
import { ASSET_REPORT_LABEL } from '../../../common/comstant/LabelConstants';
import { numberWithCommas } from '../../../common/util/NumberUtils';
import AssetReportItemTableModal, { AssetReportItemTableModalData } from './popup/AssetReportItemTableModal';
import { ASSET_TYPE, REPORT_ITEM_TYPE } from '../type/AssetReport.type';

export interface AssetStatusCardBarChartData {
  assetTotalAmount: number;
  assetSavingsAmount: number;
  assetInvestmentAmount: number;
  assetEtcAmount: number;
  assetExpectedToConsumeAmount: number;
  liabilityTotalAmount: number;
  liabilityLoanAmount: number;
  liabilityEtcAmount: number;
  liabilityExpectedToConsumeAmount: number;
}

const AssetStatusCardBarChart = ({
  assetTotalAmount,
  assetSavingsAmount,
  assetInvestmentAmount,
  assetEtcAmount,
  assetExpectedToConsumeAmount,
  liabilityLoanAmount,
  liabilityEtcAmount,
  liabilityExpectedToConsumeAmount,
  liabilityTotalAmount,
}: AssetStatusCardBarChartData) => {
  const [assetReportItemTableModalData, setAssetReportItemTableModalData] = useState<AssetReportItemTableModalData>();

  useEffect(() => {
    const assetChartDom = document.getElementById('asset-chart');
    if (!assetChartDom) return;
    if (assetChartDom) {
      const assetChart = echarts.init(assetChartDom);
      const assetOption = {
        animation: false,
        tooltip: {
          trigger: 'axis',
          triggerOn: window.innerWidth < 768 ? 'none' : 'mousemove',
          axisPointer: { type: 'shadow' },
          formatter: commonEchartsTooltipFormatter,
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: [ASSET_REPORT_LABEL.ASSET, ASSET_REPORT_LABEL.LIABILITY],
        },
        yAxis: {
          type: 'value',
        },
        series: [
          {
            name: ASSET_REPORT_LABEL.SAVINGS,
            type: 'bar',
            stack: 'total',
            data: [assetSavingsAmount, null], // 자산에는 값, 부채에는 0
            itemStyle: { color: '#3970ff' }, // red2
            label: {
              show: true,
              color: 'black',
              formatter: (params: any) => commonEchartsLabelFormatter(params, assetTotalAmount),
            },
          },
          {
            name: ASSET_REPORT_LABEL.INVESTMENT,
            type: 'bar',
            stack: 'total',
            data: [assetInvestmentAmount, null], // 자산에만 값
            itemStyle: { color: '#739aff' }, // red1
            label: {
              show: true,
              color: 'black',
              formatter: (params: any) => commonEchartsLabelFormatter(params, assetTotalAmount),
            },
          },
          {
            name: ASSET_REPORT_LABEL.LOAN,
            type: 'bar',
            stack: 'total',
            data: [null, liabilityLoanAmount],
            itemStyle: { color: '#ea464b' }, // blue1
            label: {
              show: true,
              color: 'black',
              formatter: (params: any) => commonEchartsLabelFormatter(params, liabilityTotalAmount),
            },
          },
          {
            name: ASSET_REPORT_LABEL.ETC,
            type: 'bar',
            stack: 'total',
            data: [assetEtcAmount, liabilityEtcAmount], // 부채에는 값, 자산에는 0
            itemStyle: {
              color: (params: any) => {
                // params.dataIndex: 0 은 자산, 1 은 부채
                return params.dataIndex === 0
                  ? '#8eadff' // 자산 ETC
                  : '#e97d80'; // 부채 ETC
              },
            }, // blue2
            label: {
              show: true,
              color: 'black',
              formatter: (params: any) =>
                commonEchartsLabelFormatter(params, params.dataIndex === 0 ? assetTotalAmount : liabilityTotalAmount),
            },
          },
          {
            name: ASSET_REPORT_LABEL.EXPECT_TO_CONSUME,
            type: 'bar',
            stack: 'total',
            data: [assetExpectedToConsumeAmount, liabilityExpectedToConsumeAmount],
            itemStyle: { color: '#b2b2b2' }, // blue1
            label: {
              show: true,
              color: 'black',
              formatter: (params: any) => commonEchartsLabelFormatter(params, liabilityTotalAmount),
            },
          },
          {
            name: '총합',
            type: 'bar',
            data: [assetTotalAmount, liabilityTotalAmount],
            barGap: '-100%',
            itemStyle: { color: 'transparent' },
            label: {
              show: true,
              position: 'top',
              formatter: (params: any) => {
                return `${numberWithCommas(params.data)}원`;
              },
              fontWeight: 'bold',
              fontSize: 12,
            },
            // 차트 상호작용에 방해되지 않도록
            silent: true,
          },
        ],
      } as ECBasicOption;
      assetChart.setOption(assetOption);

      assetChart.on('click', (params: any) => {
        // bar 시리즈를 클릭했을 때만
        if (params.componentType === 'series' && params.seriesType === 'bar') {
          setAssetReportItemTableModalData({
            isOpen: true,
            reportItemType:
              params.name === ASSET_REPORT_LABEL[REPORT_ITEM_TYPE.ASSET]
                ? REPORT_ITEM_TYPE.ASSET
                : REPORT_ITEM_TYPE.LIABILITY,
            onClose: () => {
              setAssetReportItemTableModalData(undefined);
            },
          });
        }
      });

      window.addEventListener('resize', () => assetChart.resize());
    }
  }, [
    assetSavingsAmount,
    assetInvestmentAmount,
    assetEtcAmount,
    assetExpectedToConsumeAmount,
    liabilityLoanAmount,
    liabilityEtcAmount,
    liabilityExpectedToConsumeAmount,
  ]);

  return (
    <>
      <div id="asset-chart" className="w-full h-80"></div>
      {assetReportItemTableModalData && <AssetReportItemTableModal {...assetReportItemTableModalData} />}
    </>
  );
};

export default AssetStatusCardBarChart;
